import { ExistingQuoteStateValue } from "./types";
import { CARRIER_VERIFICATION_INSTRUCTION } from "../../../../utils/carrierVerification";

/**
 * Carrier-facing copy for the reasons a landing hides the quote form. Keys are
 * QuoteSubmissionFailureCode values (application/quote/constants/__init__.py),
 * surfaced by the preview through classify_quote_submission_exception; they are
 * the same business reasons the carrier would see in the app, not
 * permission-boundary detail
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D6).
 *
 * Verification and insurance are handled over email, not in the app — the
 * profile editor cannot set them. These point at support the same way
 * components/Banner/components/UnverifiedCarrierBanner does, and none of them
 * claims anything about the state of a review we cannot see.
 */
export const BLOCKED_REASON_MESSAGES: Record<string, string> = {
  SHIPMENT_INACTIVE: "This shipment is no longer accepting quotes.",
  SHIPMENT_ALREADY_BOOKED: "This shipment has already been booked.",
  SHIPMENT_CANCELLED: "The shipper cancelled this shipment.",
  SHIPMENT_EXPIRED: "This shipment has expired.",
  NO_CARRIER_INSURANCE: `Your company has no insurance on file. ${CARRIER_VERIFICATION_INSTRUCTION}`,
  EXPIRED_CARRIER_INSURANCE: `Your insurance has expired. ${CARRIER_VERIFICATION_INSTRUCTION}`,
  UNVERIFIED_COMPANY: `Your company is not verified. ${CARRIER_VERIFICATION_INSTRUCTION}`,
  PAYMENT_METHOD_REQUIRED:
    "Add a payment method in Evotrux to quote on this shipment.",
  // Self-serve, unlike verification and insurance above: reactivating or moving to pay
  // per booking is something the carrier can do from the plan page.
  SUBSCRIPTION_INACTIVE:
    "Your Evotrux subscription is not active. Reactivate your plan or switch to pay per booking to keep quoting.",
  SHIPMENT_BOOKING_CANCELLED:
    "The booking on this shipment was cancelled. Log in to Evotrux to quote on it again.",
};

export const DEFAULT_BLOCKED_MESSAGE =
  "You cannot quote on this shipment right now. Log in to Evotrux for details.";

/**
 * Both live quote states send the carrier to the app — the command link only ever
 * creates a first quote (D4). Keyed by the quote status the preview reports.
 */
export const EXISTING_QUOTE_MESSAGES: Record<ExistingQuoteStateValue, string> =
  {
    [ExistingQuoteStateValue.ACTIVE]:
      "You already have an active quote on this shipment.",
    [ExistingQuoteStateValue.COUNTERED]:
      "The shipper countered your quote on this shipment.",
  };

/**
 * Copy for the recoverable QuoteSubmissionFailureCode values execute can report
 * (HTTP 200 with success: false). A separate vocabulary from the quote statuses
 * above: ACTIVE_QUOTE_EXISTS is the same situation as ACTIVE, reported when a
 * quote appears between preview and submit
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md §6).
 */
export const RECOVERABLE_OUTCOME_MESSAGES: Record<string, string> = {
  ACTIVE_QUOTE_EXISTS: "You already have a live quote on this shipment.",
};

/**
 * The recoverable codes the carrier can act on without leaving this page. Execute
 * returns them HTTP 200 with `success: false`, so nothing else would surface them
 * — without copy here the carrier presses Submit and sees no change at all.
 */
export const CORRECTABLE_OUTCOME_MESSAGES: Record<string, string> = {
  PERSONAL_INFORMATION:
    "Remove contact details (phone numbers, emails) from your note — the shipper reaches you through Evotrux.",
  PRICE_BELOW_ZERO: "Price must be greater than 0.",
  INVALID_PRICE: "Enter a valid price.",
  INVALID_PICKUP_DELIVERY_DATES:
    "Pickup start must be before or equal to the delivery ETA.",
  UNSUPPORTED_CURRENCY: "Select a supported currency.",
  UOM_MISMATCH:
    "This shipment is quoted in a different unit of measure. Log in to Evotrux to quote on it.",
  VALIDATION_ERROR: "Check the quote details and try again.",
};

export const DEFAULT_CORRECTABLE_MESSAGE =
  "We could not submit that quote. Check the details and try again, or log in to Evotrux.";

/**
 * Shown on the "no truck available" control when execute rejects a mark or undo
 * with a recoverable code that has no page-level handoff in
 * RECOVERABLE_OUTCOME_MESSAGES. The quote copy above describes fields the carrier
 * never filled in, so it never applies here.
 */
export const NO_CAPACITY_REJECTED_MESSAGE =
  "We could not save your answer. Try again, or log in to Evotrux.";

/**
 * The status `preview_command_link` returns when it refuses the link — an expired
 * or tampered token, or a carrier who can no longer read the shipment
 * (`application/commandLink/controllers.py`). The two carry different bodies: a
 * bad token gets a message, because a dead link is a dead end the recipient
 * cannot escape without being told to ask for a new one, while a permission
 * refusal stays empty per the project's rule against explaining a denial. The
 * funnel therefore reads the status, not the body, to tell a dead link apart
 * from a transport failure, and the page picks its copy from the same test — so
 * a drift here costs the carrier the right message, not just an analytics label.
 */
export const LINK_REJECTED_HTTP_STATUS = 403;

const TOO_MANY_REQUESTS_HTTP_STATUS = 429;
const SERVER_ERROR_HTTP_STATUS = 500;

/**
 * Whether a second attempt could plausibly get past what stopped this one. A null
 * status is a request that never reached the backend. `preview_command_link` also
 * answers 400 (unknown command, bad request) and 404 (the token outlives the
 * shipment or role it names) — both permanent, and both would answer a retry the
 * same way forever, so the retry copy must not be offered for them.
 *
 * Phrased as the retryable set rather than the rejected one: a status nobody
 * anticipated should read as a dead end, not as an invitation to keep trying.
 */
export function previewFailureCanBeRetried(
  failureStatus: number | null,
): boolean {
  return (
    failureStatus === null ||
    failureStatus === TOO_MANY_REQUESTS_HTTP_STATUS ||
    failureStatus >= SERVER_ERROR_HTTP_STATUS
  );
}

// Shown when the backend refused the link: nothing here is retryable.
export const LINK_REJECTED_MESSAGE =
  "This link is no longer valid. Log in to Evotrux to quote on this shipment, or contact support.";

/**
 * Shown when the preview never came back: a server error, a rate limit, or a
 * connection that dropped. The link may well still be good, so telling the
 * carrier it is dead sends a quotable load to the bin over a transient failure
 * (issue #3189 F1).
 *
 * It sends the carrier back to the email rather than asking for a refresh. This
 * page strips the token from the URL on mount, and CommandLinkLanding reads the
 * token from the URL on a fresh load, so reloading lands on "Invalid link" —
 * the dead-link wall this copy exists to keep a transient failure out of.
 * Reopening the emailed link is the one retry that carries the token.
 */
export const PREVIEW_UNAVAILABLE_MESSAGE =
  "We could not load this shipment just now. Open the link from your email again, or log in to Evotrux to quote on it.";

/**
 * What a submit-quote execute request does. Required on every request: the quote
 * and the no-capacity actions share one token and one endpoint. Mirrors
 * SubmitQuoteCommandLinkAction in
 * application/commandLink/command_links/submit_quote/constants.py; changing one is
 * a change to both.
 */
export const SubmitQuoteCommandLinkAction = {
  SUBMIT_QUOTE: "SUBMIT_QUOTE",
  MARK_NO_CAPACITY: "MARK_NO_CAPACITY",
  UNDO_NO_CAPACITY: "UNDO_NO_CAPACITY",
} as const;
export type SubmitQuoteCommandLinkAction =
  (typeof SubmitQuoteCommandLinkAction)[keyof typeof SubmitQuoteCommandLinkAction];

/**
 * The unsigned query param the email's "No Truck Available" button adds to the
 * submit-quote link. It only relabels and scrolls to the no-capacity control; it grants
 * nothing. Mirrors INTENT_QUERY_PARAM_KEY / NO_CAPACITY_INTENT in
 * application/commandLink/command_links/submit_quote/constants.py; changing one is
 * a change to both.
 */
export const SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY = "intent";
export const SUBMIT_QUOTE_NO_CAPACITY_INTENT = "no-capacity";

/**
 * The `source` the landing's analytics events carry. The same value the backend
 * stamps on quotes and no-capacity answers given through the link (QuoteSource),
 * so PostHog filters line up with the database.
 */
export const COMMAND_LINK_ANALYTICS_SOURCE = "COMMAND_LINK";
